import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheck, FiArrowRight } from 'react-icons/fi';
import useScrollAnimation from '../../hooks/useScrollAnimation';
import { settingsAPI } from '../../services/api';

const features = [
  'NEB affiliated — Play Group to Class 12',
  'Expert faculty with industry experience',
  'AV-enabled smart classrooms',
  'Holistic development focus',
  'ISA British Council award winner',
  'Consistently high SEE/SLC results'
];

const AboutSection = () => {
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation();
  const [campusImageUrl, setCampusImageUrl] = useState('');

  useEffect(() => {
    settingsAPI.get().then((res) => {
      const url = res.data?.data?.settings?.campusImage?.url;
      if (url) setCampusImageUrl(url);
    }).catch(() => {});
  }, []);

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: Text */}
          <motion.div
            ref={leftRef}
            initial={{ opacity: 0, x: -40 }}
            animate={leftVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <p className="section-subtitle mb-2">About Buddha Deep English Boarding Secondary School</p>
            <h2 className="section-title mb-6">
              Shaping Future Leaders Since 2000
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Sited in a serene and tranquil environment in Rupandehi, Nepal, BDEBSS is a premier
              academic hub for top-notch quality education and all-round development. Since its
              inception in 2000 A.D, it has carved out a niche as the best +2 school in
              Rupandehi District.
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              BDEBSS is committed to redefining modern education — fostering intellectual vigor
              with edge-cut labs, ultra-modern technology, and faculty members with industry
              experience. We make deliberate efforts to take learning beyond the classroom,
              nurturing ethical, community-driven students ready for the 21st century.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
              {features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={leftVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-secondary-50 border border-secondary-200 flex items-center justify-center flex-shrink-0">
                    <FiCheck className="w-3 h-3 text-secondary-600" />
                  </div>
                  <span className="text-gray-700 text-sm">{feature}</span>
                </motion.div>
              ))}
            </div>

            <Link
              to="/about"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-xl font-semibold hover:bg-primary-600 transition-all hover:-translate-y-0.5 shadow-lg hover:shadow-primary-500/30"
            >
              Learn More About Us
              <FiArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* Right: Image */}
          <motion.div
            ref={rightRef}
            initial={{ opacity: 0, x: 40 }}
            animate={rightVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative">
              {/* Main Image */}
              <div className="w-full h-64 sm:h-80 lg:h-96 bg-gradient-to-br from-primary-500 to-primary-700 rounded-3xl overflow-hidden flex items-center justify-center shadow-2xl">
                {campusImageUrl ? (
                  <img src={campusImageUrl} alt="School Campus" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center text-white p-8">
                    <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-14 h-14 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-serif font-bold mb-2">Buddha Deep English Boarding Secondary School</h3>
                    <p className="text-white/70 text-sm">Our School Campus</p>
                  </div>
                )}
              </div>

              {/* Floating card - Students */}
              <motion.div
                animate={{ y: [-5, 5, -5] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -bottom-4 sm:-bottom-6 left-2 sm:-left-6 bg-white rounded-2xl shadow-xl p-3 sm:p-4 border border-gray-100 z-10"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-secondary-50 rounded-xl flex items-center justify-center shrink-0">
                    <span className="text-lg sm:text-2xl font-bold text-secondary-600">A+</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">98% Pass Rate</p>
                    <p className="text-xs text-gray-500">Board Examinations</p>
                  </div>
                </div>
              </motion.div>

              {/* Floating card - Award */}
              <motion.div
                animate={{ y: [5, -5, 5] }}
                transition={{ duration: 4, repeat: Infinity, delay: 1 }}
                className="absolute -top-3 sm:-top-4 right-2 sm:-right-4 bg-white rounded-2xl shadow-xl p-3 sm:p-4 border border-gray-100 z-10"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">Best School</p>
                    <p className="text-xs text-gray-500">Award 2024</p>
                  </div>
                </div>
              </motion.div>

              {/* Decorative ring */}
              <div className="absolute -z-10 inset-4 bg-secondary-500/10 rounded-3xl transform translate-x-4 translate-y-4" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
